import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { MoreOption, MoreOptionAction } from '@components/more-options/more-options-dropdown.component';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import { GaugeGraphChannel, getDefaultScaleDivision, MAX_SCALE_DIVISION_VALUE } from 'icreate/common/models/graphs';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-gauge-graph-table-row',
    templateUrl: './gauge-graph-table-row.component.html',
    styleUrl: './gauge-graph-table-row.component.css',
    standalone: false,
})
export class GaugeGraphTableRowComponent {
    @Input() rowIndex: number;
    @Input() channel: GaugeGraphChannel;
    @Input() vesselId: string;
    @Input() optionsVisible: boolean;
    @Output() onDeleteRow: EventEmitter<void> = new EventEmitter<void>();
    @Output() onOptionsToggled: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('iconButton') iconButton: ElementRef;

    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();
    isDropdownOpen: boolean = false;

    @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
    @ViewChild('dropdownButton') dropdownButton!: ElementRef;

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (
            !this.otherOptions?.nativeElement.contains(event.target) &&
            !this.iconButton?.nativeElement.contains(event.target) &&
            this.optionsVisible
        ) {
            this.onOptionsToggled.emit(this.rowIndex);
        }

        if (
            this.isDropdownOpen &&
            !this.dropdownMenu?.nativeElement.contains(event.target) &&
            !this.dropdownButton?.nativeElement.contains(event.target)
        ) {
            this.isDropdownOpen = false;
        }
    }

    @HostListener('window:resize')
    onWindowChange() {
        if (this.optionsVisible) {
            this.onOptionsToggled.emit(this.rowIndex);
        }
    }

    async openChannelSelector() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: this.channel.channel,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: true,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.channel.channel = channelSelected;
            this.channel.scaleDivision = getDefaultScaleDivision(channelSelected);
        }
    }

    toggleDropdown() {
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    setScaleDivision(num: number) {
        this.channel.scaleDivision = num;
        this.isDropdownOpen = false;
    }

    get numbers() {
        return Array.from({ length: MAX_SCALE_DIVISION_VALUE }, (_, i) => i + 1);
    }

    openOptions() {
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clear() {
        if (this.clearIsDisabled()) return;
        this.channel.channel = null;
        this.channel.scaleDivision = null;
        this.onOptionsToggled.emit(this.rowIndex);
    }

    deleteRow() {
        this.onDeleteRow.emit();
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clearIsDisabled() {
        return !this.channel.channel && !this.channel.scaleDivision;
    }

    isScaleSelected(num) {
        return !!this.channel.scaleDivision && num === this.channel.scaleDivision;
    }

    get moreOptions(): MoreOption[] {
        return [
            {
                action: MoreOptionAction.CLEAR,
                string: 'dlg.clear',
                disabled: this.clearIsDisabled(),
            },
            {
                action: MoreOptionAction.DELETE,
                string: 'dlg.delete',
            },
        ];
    }

    onMoreOptionSelected(action: MoreOptionAction): void {
        switch (action) {
            case MoreOptionAction.CLEAR:
                this.clear();
                break;

            case MoreOptionAction.DELETE:
                this.deleteRow();
                break;
        }
    }
}
